import { useContext, useEffect, useState } from "react";
import { motion } from "framer-motion";
import { MainContext } from "components/utils/Contexts";
import { usePlatform } from "components/utils/PlatformContext";
import { removeItem, singlify } from "utils/utils";
import {
  defaultBgVariants,
  defaultPanelVariants,
} from "../common/defaultValues";
import PopUpHeader from "../common/PopUpHeader";
import SelectSchoolPanel from "./SelectSchoolList";
import ClassesCardsList from "../common/ClassesCardsList";
import { ClassListType } from "utils/interfaces";
import {
  getCollectionRequest,
  updateRecordRequest,
} from "services/apiRequests";

interface AssignClassToCoursePanelProps {
  courseIdToAssign: string;
  onClose: () => void;
}

/**
 * Element that represent the pop up panel for adding a course inside a specific class.
 * @param courseIdToAssign _id of the lesson to assign based on the selection of the `selectedLesson` parameter.
 * @param setShowPopUp Function used to define if the pop should be showed or closed.
 * @returns
 */
const AssignClassToCoursePanel: React.FC<AssignClassToCoursePanelProps> = ({
  courseIdToAssign,
  onClose,
}) => {
  // Get the context parameters
  const { classesTerminology, schoolTerminology } = usePlatform();
  const { userRole } = useContext(MainContext);

  // States definition
  const [popUpState, setPopUpState] = useState<"main" | "select-school">(
    userRole === "master admin" ? "select-school" : "main",
  );
  const [selectedClasses, setSelectedClasses] = useState<string[]>([]);

  /**
   * Definition of the function when pressed back button
   */
  const handlePressBack = () => {
    // Check the state of main for master admins
    if (popUpState === "main" && userRole === "master admin") {
      // Come back to the select school options
      setPopUpState("select-school");
    }
    // Otherwise, close the pop up
    else onClose();
  };

  /**
   * Get the current
   */
  useEffect(() => {
    getCollectionRequest(
      `/api/courses/assigned-classes?id=${courseIdToAssign}`,
    ).then((response) => {
      if (response?.successful) {
        setSelectedClasses(response.content);
      }
    });
  }, []);

  /**
   * Adding a listener for keyboard shortcuts
   */
  useEffect(() => {
    // Function to use on escape
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") handlePressBack();
    };

    // Adding the lister
    document.addEventListener("keydown", handleKeyDown);

    // Cleanup
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [popUpState]);

  /**
   * Definition of the Add or remove action
   * @param id of the selected id in the pop up menu
   * @param action "add" or "remove" "selectedData._id" from "id"
   */
  const selectClassAction = async (
    classItem: ClassListType,
    action: "add" | "remove",
  ) => {
    // Send the update request if we are on the assign lesson to class
    // And add or remove the data depending on the case
    const response = await updateRecordRequest(
      {
        classId: classItem._id,
        courseId: courseIdToAssign,
        action: action,
      },
      "/api/classes/assign-course",
    );

    // Check if the response was successful
    if (!response.successful) {
      alert(response.message);
      return;
    }

    // Add to the current selected contact data ids
    if (action === "add") {
      // Add to the ids list
      setSelectedClasses([...new Set([...selectedClasses, classItem._id])]);
    }
    // Or delete from it
    else if (action === "remove") {
      setSelectedClasses(removeItem(selectedClasses, classItem._id));
    }
  };

  return (
    <>
      <motion.div
        className="fixed inset-0 w-full h-full bg-neutral-500 bg-opacity-40 backdrop-blur-[2px] z-20 flex flex-col flex-grow"
        variants={defaultBgVariants}
        initial="initial"
        animate="animate"
        exit="exit"
        transition={{ duration: 0.1, ease: "easeOut" }}
        onClick={onClose}
      />
      <motion.div
        className="fixed w-[462px] h-fit max-h-[90%] left-0 right-0 ml-auto mr-auto px-6 pt-6 pb-8 bottom-5 bg-white rounded-3xl flex-col justify-start items-start gap-6 inline-flex z-30"
        variants={defaultPanelVariants}
        initial="initial"
        animate="animate"
        exit="exit"
        transition={{ duration: 0.1, ease: "easeOut" }}
      >
        <div className="relative w-full h-fit max-h-[80vh] flex-col justify-start items-start gap-6 inline-flex">
          <PopUpHeader
            title={
              popUpState === "select-school"
                ? `Select ${singlify(schoolTerminology).toLowerCase()}`
                : `Assign to ${singlify(classesTerminology)}`
            }
            handlePressBack={handlePressBack}
          />
          {popUpState === "main" && (
            <ClassesCardsList
              selectedClasses={selectedClasses as any}
              closeOnceClickOnCard={false}
              onDoneCloseClick={onClose}
              showDoneButton={true}
              selectAction={selectClassAction}
            />
          )}
          {popUpState === "select-school" && (
            <SelectSchoolPanel setPopUpState={setPopUpState} />
          )}
        </div>
      </motion.div>
    </>
  );
};

export default AssignClassToCoursePanel;
